import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import {
  LogOut,
  RefreshCw,
  Loader2,
  Save,
  Zap,
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  Info as InfoIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import SEO from "@/components/SEO";

export default function AdminSettingsPage() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState({
    pixel_id: "",
    access_token: "",
    api_version: "v22.0",
    test_event_code: "",
    enabled: true,
  });
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/settings/meta");
      setSettings(data);
      setForm({
        pixel_id: data.pixel_id || "",
        access_token: "",
        api_version: data.api_version || "v22.0",
        test_event_code: data.test_event_code || "",
        enabled: !!data.enabled,
      });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onChange = (key) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const save = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        pixel_id: form.pixel_id,
        api_version: form.api_version,
        test_event_code: form.test_event_code,
        enabled: form.enabled,
      };
      if (form.access_token && form.access_token.trim()) {
        payload.access_token = form.access_token.trim();
      }
      const { data } = await api.put("/admin/settings/meta", payload);
      setSettings(data);
      setForm((f) => ({ ...f, access_token: "" }));
      setNotice("Configuración guardada correctamente.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setError("");
    setTestResult(null);
    try {
      // Guardar cambios pendientes antes del test para que Meta reciba lo que ve el admin
      if (isDirty) await save();
      const { data } = await api.post("/admin/settings/meta/test", {});
      setTestResult(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Error enviando evento de prueba");
    } finally {
      setTesting(false);
    }
  };

  const isDirty =
    settings && (
      form.pixel_id !== (settings.pixel_id || "") ||
      form.api_version !== (settings.api_version || "v22.0") ||
      form.test_event_code !== (settings.test_event_code || "") ||
      form.enabled !== !!settings.enabled ||
      (form.access_token && form.access_token.trim().length > 0)
    );

  return (
    <div className="min-h-screen bg-brand-bg text-brand-ink">
      <SEO title="Configuración · Club Amistad" description="Panel de configuración admin" path="/admin/configuracion" noindex />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-brand-line">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" aria-label="Club Amistad — Inicio">
            <img src="/club-amistad-logo.png" alt="Club Amistad" className="h-11 w-auto" />
            <span className="hidden sm:inline-flex items-center px-2.5 py-1 rounded-full bg-brand-primary/8 text-brand-primary text-[10px] uppercase tracking-wider font-medium">
              Mini-CRM
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="btn-ghost !py-2 !px-4" data-testid="refresh-settings-btn">
              <RefreshCw className="h-4 w-4" /> Recargar
            </button>
            <span className="hidden md:inline text-sm text-brand-muted">{user?.email}</span>
            <button onClick={logout} className="btn-primary !py-2 !px-4" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </div>
        {/* Subnav */}
        <div className="max-w-7xl mx-auto px-6 pb-2 flex gap-1">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) =>
              `px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                isActive
                  ? "border-brand-primary text-brand-primary font-medium"
                  : "border-transparent text-brand-muted hover:text-brand-primary"
              }`
            }
            data-testid="nav-leads"
          >
            Leads
          </NavLink>
          <NavLink
            to="/admin/configuracion"
            className={({ isActive }) =>
              `px-4 py-2 text-sm rounded-t-lg border-b-2 transition-colors ${
                isActive
                  ? "border-brand-primary text-brand-primary font-medium"
                  : "border-transparent text-brand-muted hover:text-brand-primary"
              }`
            }
            data-testid="nav-settings"
          >
            Configuración
          </NavLink>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="overline">Configuración</p>
          <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-3">
            Meta Conversions API
          </h1>
          <p className="text-brand-muted mt-2 max-w-2xl">
            Configura la integración server-side con Meta (Facebook/Instagram). Los cambios se aplican
            en unos segundos, sin reiniciar el servidor.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2" data-testid="settings-error">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {notice && !error && (
          <div className="mb-6 p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm flex items-start gap-2" data-testid="settings-notice">
            <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{notice}</span>
          </div>
        )}

        {loading || !settings ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
          </div>
        ) : (
          <div className="space-y-8">
            <section className="card-soft p-6">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2 className="font-outfit text-xl font-semibold text-brand-primary flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5" /> Credenciales
                  </h2>
                  <p className="text-sm text-brand-muted mt-1">
                    Los eventos de Lead se enviarán con estas credenciales.
                  </p>
                </div>
                <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                  <span className="text-sm text-brand-muted">Activo</span>
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={form.enabled}
                    onChange={onChange("enabled")}
                    data-testid="toggle-enabled"
                  />
                  <span className="w-11 h-6 bg-stone-200 rounded-full peer-checked:bg-emerald-500 relative transition-colors">
                    <span className="absolute left-0.5 top-0.5 h-5 w-5 bg-white rounded-full shadow transition-transform peer-checked:translate-x-5" />
                  </span>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="field-label">Pixel ID</label>
                  <input
                    type="text"
                    className="field-input"
                    value={form.pixel_id}
                    onChange={onChange("pixel_id")}
                    placeholder="1234567890123456"
                    data-testid="input-pixel-id"
                  />
                </div>
                <div>
                  <label className="field-label">API version</label>
                  <input
                    type="text"
                    className="field-input"
                    value={form.api_version}
                    onChange={onChange("api_version")}
                    placeholder="v22.0"
                    data-testid="input-api-version"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="field-label">
                    Access Token
                    {settings.access_token_set && (
                      <span className="ml-2 text-xs text-brand-muted font-normal">
                        Actual: <code>{settings.access_token_masked}</code>
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    className="field-input font-mono"
                    value={form.access_token}
                    onChange={onChange("access_token")}
                    placeholder={settings.access_token_set ? "Deja vacío para conservar el actual" : "EAA…"}
                    autoComplete="new-password"
                    data-testid="input-access-token"
                  />
                  <p className="text-xs text-brand-muted mt-1.5">
                    Solo se guarda si escribes algo. El token nunca se muestra completo por seguridad.
                  </p>
                </div>
              </div>
            </section>

            <section className="card-soft p-6">
              <h2 className="font-outfit text-xl font-semibold text-brand-primary flex items-center gap-2">
                <Zap className="h-5 w-5" /> Modo de pruebas
              </h2>
              <p className="text-sm text-brand-muted mt-1 mb-5">
                Introduce un <strong>Test Event Code</strong> del panel Meta Events Manager para que los eventos aparezcan sólo en la pestaña "Prueba de eventos".
                <br />
                <span className="text-red-600 font-medium">Déjalo vacío en producción</span> para que los eventos cuenten como conversiones reales.
              </p>

              <div>
                <label className="field-label">Test Event Code</label>
                <input
                  type="text"
                  className="field-input font-mono"
                  value={form.test_event_code}
                  onChange={onChange("test_event_code")}
                  placeholder="TEST12345 (opcional)"
                  data-testid="input-test-event-code"
                />
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={save}
                  disabled={saving || !isDirty}
                  className="btn-primary !py-2.5"
                  data-testid="save-settings-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {saving ? "Guardando…" : "Guardar cambios"}
                </button>
                <button
                  onClick={runTest}
                  disabled={testing || !form.pixel_id}
                  className="btn-ghost !py-2.5"
                  data-testid="send-test-event-btn"
                >
                  {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
                  {testing ? "Enviando…" : "Enviar evento de prueba"}
                </button>
              </div>

              {testResult && (
                <div
                  className={`mt-5 p-4 rounded-xl border text-sm ${
                    testResult.ok
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-red-50 border-red-200 text-red-800"
                  }`}
                  data-testid="test-result"
                >
                  <div className="flex items-start gap-2">
                    {testResult.ok ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    )}
                    <div className="space-y-1.5">
                      <p className="font-medium">
                        {testResult.ok
                          ? `Evento enviado a Meta correctamente${
                              testResult.test_event_code ? ` (código ${testResult.test_event_code})` : ""
                            }.`
                          : "Meta devolvió un error."}
                      </p>
                      <p className="text-xs opacity-80">event_id: <code>{testResult.event_id}</code></p>
                      <pre className="mt-2 bg-white/60 rounded-md p-3 text-xs overflow-x-auto">
{JSON.stringify(testResult.meta_response, null, 2)}
                      </pre>
                      {testResult.ok && testResult.test_event_code && (
                        <p className="text-xs pt-1">
                          Comprueba el evento en Meta Events Manager →{" "}
                          <a
                            href="https://business.facebook.com/events_manager2/list/dataset"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline"
                          >
                            Prueba de eventos
                          </a>
                          .
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </section>

            <section className="card-soft p-6 bg-brand-primary/[0.03] border-brand-primary/10">
              <h3 className="font-outfit text-base font-semibold text-brand-primary flex items-center gap-2">
                <InfoIcon className="h-4 w-4" /> Cómo obtener el Test Event Code
              </h3>
              <ol className="text-sm text-brand-ink/85 mt-2 space-y-1 list-decimal list-inside">
                <li>Entra en Meta Events Manager y selecciona el dataset del Pixel.</li>
                <li>Abre la pestaña <em>"Prueba de eventos"</em> ("Test Events").</li>
                <li>Copia el código que aparece (ej. <code>TEST78901</code>) y pégalo arriba.</li>
                <li>Guarda y pulsa <strong>Enviar evento de prueba</strong> — debería aparecer en unos segundos.</li>
                <li>Cuando termines, vacía el campo para volver a producción.</li>
              </ol>
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
